/** Generate Audio — Canonical Generation Card (Result-first).
 *
 * VISTOERA_GENERATE_AUDIO_CANONICAL_GENERATION_CARD_VOICE_TEMPLATE_ASSET_RESULT_FIRST_V2
 *
 * HARD vertical order:
 * HEADER → RESULT → RELATED ASSETS (Voice Template) → PARAMETERS → INPUT TEXT → FOOTER
 *
 * Collapsed (default browse): HEADER + RESULT only.
 * Expand uses CanvasBoard `expandedNodeId` (single expanded Generation Card).
 * External Quick Actions: CARD RIGHT OUTSIDE via AudioWorkflowRail + Result-zone hover bridge.
 *
 * Backend Canvas TTS contract remains: text + voice_template_id | system_voice_key.
 * No fake Language/Speed/Format selectors (not in Canvas API).
 * Voice identity source = selected Voice Template (ACTIVE_VOICE_TEMPLATE_COUNT=1).
 * Diverge not rendered (AUDIO_DIVERGE_SUPPORTED=false).
 */

import {
  ExclamationCircleFilled,
  CheckCircleFilled,
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  SoundOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  generateCanvasAudio,
  fetchVoiceTemplateExecutionReadiness,
  type SaasApiError,
  type AudioGenerationParameterCapabilityResponse,
} from '../../../../api'
import { loadAudioGenerationParameterCapabilityDeduped } from '../canvasSharedConfigLoaders'
import CanvasActionPopover from '../CanvasActionPopover'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import { downloadCanvasMedia } from '../canvasDownload'
import { mediaPreviewHandleTopPx, MEDIA_CARD_HEADER_HEIGHT_PX } from '../canvasNodeHandleAnchor'
import {
  buildExplicitProviderSelectionPatch,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { audioOutputReady, audioOutputStale } from '../canvasVoiceTemplateBinding'
import { primaryBlockingReason } from '../generationReadiness'
import {
  evaluateGenerateAudioReadiness,
  generateAudioDisabledReasonCode,
  resolveGenerateAudioEffectiveText,
} from '../generateAudioReadiness'
import {
  formatGenerateAudioError,
  formatPersistedGenerateAudioError,
  isGenerationErrorRetryable,
  isVoiceBindingGenerateAudioError,
  resolveVoiceTemplateUnavailableState,
} from '../generateAudioError'
import { AUDIO_TEXT_INPUT_HANDLE, AUDIO_VOICE_TEMPLATE_IN_HANDLE, GENERIC_INPUT_HANDLE } from '../dataflow'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { generationSlotIngressShellClass } from '../generationSlotIngress'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import VoiceAudioPlayer from '../../../virtualIp/VoiceAudioPlayer'
import { pauseOtherCanvasAudio } from '../canvasAudioPlaybackCoordinator'
import AudioWorkflowRail from './AudioWorkflowRail'
import { audioAssetPickerInitialSelectionFromVoiceTemplateId } from '../../../../components/AudioAssetPicker/audioAssetPickerContract'
import {
  AUDIO_GENERATION_CAPABILITY,
  AUDIO_SKILL_KEY,
  AUDIO_SKILL_VERSION,
  CANVAS_AUDIO_TEXT_MAX_LENGTH,
  appendAudioGenerationHistoryEntry,
  buildAudioGenerationPayload,
  canvasAudioPlaybackUrl,
  normalizeVoiceGenerationParameterClientSide,
  type AudioGenerationHistoryEntry,
} from '../audioGenerationProvider'
import type { FlowNodeData } from '../types'

import { generationNodeCardWidth } from '../generationNodeWidthPresets'
import { resolveGenerationCardWidth } from '../canvasCollapsedResultFrameContract'

const NODE_CARD_WIDTH = generationNodeCardWidth('generate_voice')
const RESULT_AREA_HEIGHT = 148

type ConnectedVoiceTemplate = {
  templateId: string
  label: string
  providerVoiceId: string | null
  ready: boolean
  executable?: boolean
}

type ConnectedText = {
  text: string
  sourceLabel: string
  ready: boolean
}

type VoiceSelection =
  | { kind: 'system'; systemVoiceKey: string; label: string }
  | { kind: 'template'; templateId: string; label: string }
  | null

function GenerateAudioNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { mediaHeader } = useCanvasGenerationI18n()
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & {
    workspaceId?: string
    projectId?: string
    connectedVoiceTemplate?: ConnectedVoiceTemplate | null
    connectedText?: ConnectedText | null
    onCreateVoiceTemplateNode?: (sourceNodeId: string) => void
    onOpenAudioAssetPicker?: FlowNodeData['onOpenAudioAssetPicker']
    onQuickCreateGenerateVideo?: (sourceNodeId: string) => void
    onDuplicateForRegenerate?: (sourceNodeId: string) => void
    pendingVoiceTemplateCandidateExists?: boolean
  }
  const def = useNodeDefinition(nodeData.nodeType)
  const displayLabel = nodeLabel(nodeData.nodeType, def.label) || t('generateAudio.title')
  const workspaceId = nodeData.workspaceId || ''
  const projectId = nodeData.projectId || ''
  const fields = nodeData.fields || {}

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const localText = (fields.text as string | undefined) ?? ''
  const selectedVoiceTemplateId = (fields.selectedVoiceTemplateId as string | null | undefined) ?? null
  const selectedSystemVoiceKey = (fields.selectedSystemVoiceKey as string | null | undefined) ?? null
  const selectedVoiceLabel = (fields.selectedVoiceLabel as string | null | undefined) ?? null
  const selectedVoiceTemplateExecutable =
    (fields.selectedVoiceTemplateExecutable as boolean | null | undefined) ?? null
  const status = (fields.status as string | undefined) ?? 'READY'
  const audioAssetId = (fields.audioAssetId as string | null | undefined) ?? null
  const previousAudioAssetId = (fields.previousAudioAssetId as string | null | undefined) ?? null
  const durationMs = (fields.durationMs as number | null | undefined) ?? null
  const errorMessage = (fields.errorMessage as string | null | undefined) ?? null
  const errorCode = (fields.errorCode as string | null | undefined) ?? null
  const needsRegeneration = Boolean(fields.needsRegeneration)
  const pendingCandidateNodeId = (fields.pendingVoiceTemplateCandidateNodeId as string | null | undefined) ?? null
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null | undefined) ?? null
  const skillSelectionSource = (fields.skillSelectionSource as string | undefined) || 'AUTO'
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as AudioGenerationHistoryEntry[])
    : []

  const connectedVoice = nodeData.connectedVoiceTemplate
  const connectedText = nodeData.connectedText
  const [voiceTemplateExecutable, setVoiceTemplateExecutable] = useState<boolean | null>(
    selectedVoiceTemplateExecutable,
  )

  const [submitError, setSubmitError] = useState<SaasApiError | null>(null)
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLButtonElement | null>(null)
  const [resultZoneActive, setResultZoneActive] = useState(false)
  const resultZoneLeaveTimerRef = useRef<number | null>(null)
  const runBtnRef = useRef<HTMLButtonElement>(null)
  const submitLockRef = useRef(false)
  const expandedBodyRef = useRef<HTMLDivElement | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  const {
    options: providerOptions,
    defaultProviderOptionId,
    error: providerOptionsError,
    loading: providerOptionsLoading,
  } = useCapabilityProviderOptions(workspaceId, AUDIO_GENERATION_CAPABILITY, 'generate_voice')

  const resolvedProviderOptionId = useMemo(
    () =>
      resolveCapabilityProviderOptionId({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
        serverDefaultProviderOptionId: defaultProviderOptionId,
      }),
    [defaultProviderOptionId, providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((o) => ({
        value: o.provider_option_id,
        label: o.frontend_display_name,
        title: o.frontend_description || o.frontend_display_name,
        description: o.frontend_description,
        disabled: !isProviderOptionSelectable(o),
      })),
    [providerOptions],
  )

  // VistoeraTASK-00217 — Capability-driven Pitch/Speed. `GenerateAudioNode`
  // fetches its own copy via the same single-flight-deduped loader
  // `CanvasBoard` uses for the Voice Template Parameter Memory bind-time
  // fallback, so N nodes + the board collapse into one real request per
  // workspace (see `canvasSharedConfigLoaders.ts`). Never fabricates
  // bounds while loading/unavailable — Sliders simply stay hidden.
  const [parameterCapability, setParameterCapability] = useState<AudioGenerationParameterCapabilityResponse | null>(
    null,
  )
  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    loadAudioGenerationParameterCapabilityDeduped(workspaceId)
      .then((data) => {
        if (!cancelled) setParameterCapability(data)
      })
      .catch(() => {
        /* Sliders degrade to hidden until the capability loads. */
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const pitchCapability = parameterCapability?.pitch ?? null
  const speedCapability = parameterCapability?.speed ?? null
  const persistedPitch = (fields.pitch as number | null | undefined) ?? null
  const persistedSpeed = (fields.speed as number | null | undefined) ?? null
  // Precedence (spec §11): (1) node's own currently-saved value, else
  // (2) [applied at bind time by `CanvasBoard.onBindAudioVoiceTemplate`,
  // already landed in `persistedPitch`/`persistedSpeed` by then], else
  // (3) the active Provider Capability's own default.
  const effectivePitch = normalizeVoiceGenerationParameterClientSide(persistedPitch, pitchCapability)
  const effectiveSpeed = normalizeVoiceGenerationParameterClientSide(persistedSpeed, speedCapability)

  // Local draft state while actively dragging — committed to the real
  // persisted field (via `patchFields`, which flows into the existing
  // 800ms debounced autosave) ONLY on release. Strictly forbidden:
  // writing on every `onChange` tick (spec §16 — no per-pixel network
  // writes / DB writes / capability or preference re-fetches).
  const [pitchDraft, setPitchDraft] = useState<number | null>(null)
  const [speedDraft, setSpeedDraft] = useState<number | null>(null)
  const displayedPitch = pitchDraft ?? effectivePitch
  const displayedSpeed = speedDraft ?? effectiveSpeed


  const isBusy = status === 'SUBMITTING' || status === 'GENERATING'
  const hasResult = status === 'COMPLETED' && !!audioAssetId
  /** Keep playing previous/current artifact while regenerating or stale. */
  const previewAudioAssetId =
    audioAssetId || (isBusy || needsRegeneration ? previousAudioAssetId : null) || null
  const showActivePreview = Boolean(previewAudioAssetId)
  const playbackUrl = previewAudioAssetId ? canvasAudioPlaybackUrl(workspaceId, previewAudioAssetId) : ''

  const effectiveText = useMemo(
    () => resolveGenerateAudioEffectiveText({ localText, connectedText: connectedText ?? null }),
    [connectedText, localText],
  )

  const usingConnectedText = Boolean(connectedText?.ready && connectedText.text.trim())

  const effectiveVoice = useMemo((): VoiceSelection => {
    if (connectedVoice?.ready && connectedVoice.templateId) {
      return { kind: 'template', templateId: connectedVoice.templateId, label: connectedVoice.label }
    }
    if (selectedVoiceTemplateId) {
      return { kind: 'template', templateId: selectedVoiceTemplateId, label: selectedVoiceLabel || '' }
    }
    if (selectedSystemVoiceKey) {
      return { kind: 'system', systemVoiceKey: selectedSystemVoiceKey, label: selectedVoiceLabel || '' }
    }
    return null
  }, [connectedVoice, selectedVoiceLabel, selectedSystemVoiceKey, selectedVoiceTemplateId])

  const activeTemplateLabel = useMemo(() => {
    if (connectedVoice?.ready) return connectedVoice.label
    if (effectiveVoice?.label) return effectiveVoice.label
    return null
  }, [connectedVoice, effectiveVoice])

  // VistoeraTASK-00074 P4/P14 — the "关联资产" chip previously showed the
  // SAME generic "已连接" label regardless of whether the active voice
  // identity came from a live Canvas edge or from the Asset Picker,
  // leaving the user with no way to tell the two apart (spec §4 "不要让
  // 用户无法判断声音来源"). Never a third/independent source vocabulary —
  // reuses this node's own existing `connectedVoice.ready` /
  // `selectedVoiceTemplateId` / `selectedSystemVoiceKey` precedence
  // fields verbatim, purely for display.
  const voiceSourceLabelKey = useMemo(() => {
    if (connectedVoice?.ready) return 'generateAudio.connected.fromCanvas'
    if (selectedVoiceTemplateId) return 'generateAudio.connected.fromLibrary'
    if (selectedSystemVoiceKey) return 'generateAudio.connected.fromSystem'
    return 'generateAudio.connected.label'
  }, [connectedVoice, selectedSystemVoiceKey, selectedVoiceTemplateId])

  const voiceValid = Boolean(
    (connectedVoice?.ready && connectedVoice.templateId) || selectedVoiceTemplateId || selectedSystemVoiceKey,
  )

  const readiness = useMemo(
    () =>
      evaluateGenerateAudioReadiness({
        fields: fields as Record<string, unknown>,
        localText,
        connectedText: connectedText ?? null,
        connectedVoice: connectedVoice ?? null,
        selectedVoiceTemplateId,
        selectedSystemVoiceKey,
        selectedVoiceTemplateExecutable: connectedVoice?.ready
          ? connectedVoice.executable ?? voiceTemplateExecutable
          : voiceTemplateExecutable ?? selectedVoiceTemplateExecutable,
        persistedErrorMessage: errorMessage,
        persistedErrorCode: errorCode,
        providerOptions,
        providerLoading: providerOptionsLoading,
        providerError: providerOptionsError,
        serverDefaultProviderOptionId: defaultProviderOptionId,
        isBusy,
      }),
    [
      connectedText,
      connectedVoice,
      defaultProviderOptionId,
      fields,
      isBusy,
      localText,
      providerOptions,
      providerOptionsError,
      providerOptionsLoading,
      selectedSystemVoiceKey,
      selectedVoiceTemplateId,
      errorMessage,
      errorCode,
      selectedVoiceTemplateExecutable,
      voiceTemplateExecutable,
    ],
  )
  const canGenerate = readiness.ready
  const disabledReasonCode = generateAudioDisabledReasonCode(readiness)
  const primaryBlock = primaryBlockingReason(readiness)
  const blockingHint = primaryBlock ? t(primaryBlock.messageKey) : undefined
  const voiceTemplateUnavailable = resolveVoiceTemplateUnavailableState({
    voiceBindingBlocked: disabledReasonCode === 'VOICE_TEMPLATE_UNAVAILABLE',
    voiceTemplateExecutable,
    selectedVoiceTemplateExecutable,
    errorMessage,
    errorCode,
  })
  const displayErrorMessage =
    status === 'FAILED'
      ? formatPersistedGenerateAudioError({ message: errorMessage, errorCode }, t) ||
        (submitError ? formatGenerateAudioError(submitError, t) : null) ||
        t('generateAudio.errors.generateFailed')
      : submitError
        ? formatGenerateAudioError(submitError, t)
        : null
  const showRetryAction =
    status === 'FAILED' &&
    isGenerationErrorRetryable({ message: errorMessage, errorCode, apiError: submitError })
  const showPendingCandidateHint = Boolean(
    pendingCandidateNodeId && nodeData.pendingVoiceTemplateCandidateExists !== false,
  )

  const cardWidth = useMemo(
    () =>
      resolveGenerationCardWidth({
        expanded: showExpanded,
        expandedWidthPx: NODE_CARD_WIDTH,
        hasGeneratedResult: showActivePreview,
        strategy: 'audio',
      }),
    [showActivePreview, showExpanded],
  )
  const handleTop = mediaPreviewHandleTopPx(RESULT_AREA_HEIGHT, MEDIA_CARD_HEADER_HEIGHT_PX)
  const audioReadyForVideo = audioOutputReady(fields as Record<string, unknown>)
  const audioStaleForVideo = audioOutputStale(fields as Record<string, unknown>)
  const showVideoQuickAction = audioReadyForVideo || Boolean(previewAudioAssetId && hasResult)

  const activateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
      resultZoneLeaveTimerRef.current = null
    }
    setResultZoneActive(true)
  }, [])

  const deactivateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
    }
    resultZoneLeaveTimerRef.current = window.setTimeout(() => {
      setResultZoneActive(false)
      resultZoneLeaveTimerRef.current = null
    }, 120)
  }, [])

  useEffect(
    () => () => {
      if (resultZoneLeaveTimerRef.current != null) {
        window.clearTimeout(resultZoneLeaveTimerRef.current)
      }
    },
    [],
  )

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  // CASE F — Provider switch normalization. Whenever the active
  // Capability's own bounds change (e.g. a future multi-adapter registry
  // resolves a DIFFERENT real Provider), any already-persisted value
  // that is now out-of-range against the NEW bounds must be clamped —
  // never forwarded stale/out-of-range to the adapter. Also covers the
  // "Provider newly reports this parameter unsupported" case by clearing
  // the stale stored value outright (never silently kept around for a
  // resurrected Provider to misinterpret later).
  useEffect(() => {
    if (!parameterCapability) return
    const patch: Record<string, unknown> = {}
    if (persistedPitch !== null) {
      if (!pitchCapability?.supported) {
        patch.pitch = null
      } else {
        const clamped = normalizeVoiceGenerationParameterClientSide(persistedPitch, pitchCapability)
        if (clamped !== persistedPitch) patch.pitch = clamped
      }
    }
    if (persistedSpeed !== null) {
      if (!speedCapability?.supported) {
        patch.speed = null
      } else {
        const clamped = normalizeVoiceGenerationParameterClientSide(persistedSpeed, speedCapability)
        if (clamped !== persistedSpeed) patch.speed = clamped
      }
    }
    if (Object.keys(patch).length > 0) patchFields(patch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterCapability, pitchCapability, speedCapability])

  useEffect(() => {
    setVoiceTemplateExecutable(selectedVoiceTemplateExecutable)
  }, [selectedVoiceTemplateExecutable, selectedVoiceTemplateId])

  useEffect(() => {
    if (!workspaceId || selectedSystemVoiceKey) {
      setVoiceTemplateExecutable(null)
      return
    }
    const templateId =
      connectedVoice?.ready && connectedVoice.templateId
        ? connectedVoice.templateId
        : selectedVoiceTemplateId
    if (!templateId) {
      setVoiceTemplateExecutable(null)
      return
    }
    if (connectedVoice?.ready && connectedVoice.executable === false) {
      setVoiceTemplateExecutable(false)
      return
    }
    let cancelled = false
    void fetchVoiceTemplateExecutionReadiness(workspaceId, templateId)
      .then((res) => {
        if (cancelled) return
        setVoiceTemplateExecutable(res.data.executable)
        if (res.data.executable === false) {
          patchFields({ selectedVoiceTemplateExecutable: false })
        }
      })
      .catch(() => {
        if (!cancelled) setVoiceTemplateExecutable(null)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, connectedVoice, selectedVoiceTemplateId, selectedSystemVoiceKey])

  const markStaleIfHasResult = useCallback(
    (patch: Record<string, unknown>) => {
      const live = fieldsRef.current
      const liveAssetId = live.audioAssetId as string | null | undefined
      const liveStatus = live.status as string | undefined
      if (liveStatus === 'COMPLETED' && liveAssetId) {
        return { ...patch, needsRegeneration: true, previousAudioAssetId: liveAssetId }
      }
      return patch
    },
    [],
  )

  const patchText = useCallback(
    (text: string) => {
      const hadResult = status === 'COMPLETED' && !!audioAssetId
      patchFields({
        text,
        ...(hadResult && text.trim() !== localText.trim() ? markStaleIfHasResult({}) : {}),
      })
    },
    [audioAssetId, localText, markStaleIfHasResult, patchFields, status],
  )

  // VistoeraTASK-00217 — Slider drag-release commit. `onChange` while
  // dragging ONLY updates local draft state (no network/DB write, no
  // capability/preference re-fetch — spec §16). The real persisted write
  // happens exactly once, here, on `onMouseUp`/`onTouchEnd`/`onBlur`
  // (native range input "commit" moments), flowing through the same
  // 800ms-debounced autosave every other Canvas field edit uses.
  const commitPitch = useCallback(() => {
    if (pitchDraft === null) return
    const hadResult = status === 'COMPLETED' && !!audioAssetId
    patchFields({
      pitch: pitchDraft,
      ...(hadResult && pitchDraft !== persistedPitch ? markStaleIfHasResult({}) : {}),
    })
    setPitchDraft(null)
  }, [audioAssetId, markStaleIfHasResult, patchFields, persistedPitch, pitchDraft, status])

  const commitSpeed = useCallback(() => {
    if (speedDraft === null) return
    const hadResult = status === 'COMPLETED' && !!audioAssetId
    patchFields({
      speed: speedDraft,
      ...(hadResult && speedDraft !== persistedSpeed ? markStaleIfHasResult({}) : {}),
    })
    setSpeedDraft(null)
  }, [audioAssetId, markStaleIfHasResult, patchFields, persistedSpeed, speedDraft, status])

  const openVoiceTemplateAssetPicker = useCallback(() => {
    if (isBusy || connectedVoice?.ready || !nodeData.onOpenAudioAssetPicker) return
    nodeData.onOpenAudioAssetPicker({
      nodeId: id,
      binding: 'voice_template',
      allowedCategories: ['VOICE_TEMPLATE'],
      initialCategory: 'VOICE_TEMPLATE',
      presentationType: 'VOICE_TEMPLATE',
      lockPresentationType: true,
      initialSelection: audioAssetPickerInitialSelectionFromVoiceTemplateId(selectedVoiceTemplateId),
    })
  }, [connectedVoice?.ready, id, isBusy, nodeData, selectedVoiceTemplateId])

  const runGenerate = useCallback(async () => {
    if (!workspaceId || !projectId || !canGenerate || submitLockRef.current) return
    submitLockRef.current = true
    setSubmitError(null)
    const live = fieldsRef.current
    const priorAssetId = (live.audioAssetId as string | null | undefined) || null
    const priorHistory = Array.isArray(live.generationHistory)
      ? (live.generationHistory as AudioGenerationHistoryEntry[])
      : []
    patchFields({
      status: 'GENERATING',
      errorMessage: null,
      ...(priorAssetId ? { previousAudioAssetId: priorAssetId } : {}),
    })
    try {
      const voiceTemplateId =
        connectedVoice?.ready && connectedVoice.templateId
          ? connectedVoice.templateId
          : selectedVoiceTemplateId
      const systemVoiceKey = connectedVoice?.ready ? null : selectedSystemVoiceKey
      // CASE H — Regenerate must use the CURRENT node parameters at
      // click time, never a stale earlier snapshot. `displayedPitch`/
      // `displayedSpeed` already resolve (draft-while-dragging >
      // persisted node value > Provider Capability default), so this is
      // always exactly what the Slider shows the user right now.
      const payload = buildAudioGenerationPayload({
        text: effectiveText,
        voiceTemplateId,
        systemVoiceKey,
        pitch: displayedPitch,
        speed: displayedSpeed,
      })
      const res = await generateCanvasAudio(workspaceId, projectId, id, payload)
      const nextHistory =
        priorAssetId && live.status === 'COMPLETED'
          ? appendAudioGenerationHistoryEntry(priorHistory, {
              version: priorHistory.length + 1,
              audioAssetId: priorAssetId,
              artifactId: (live.artifactId as string | null | undefined) ?? null,
              textSnapshot: String(live.text || ''),
              voiceTemplateId: (live.selectedVoiceTemplateId as string | null | undefined) ?? null,
              systemVoiceKey: (live.selectedSystemVoiceKey as string | null | undefined) ?? null,
              voiceLabel: (live.selectedVoiceLabel as string | null | undefined) ?? null,
              providerOptionId: (live.providerOptionId as string | null | undefined) ?? null,
              skillKey: live.skillSelectionSource === 'EXPLICIT' ? AUDIO_SKILL_KEY : 'AUTO',
              skillVersion: live.skillSelectionSource === 'EXPLICIT' ? AUDIO_SKILL_VERSION : null,
              durationMs: (live.durationMs as number | null | undefined) ?? null,
              completedAt: new Date().toISOString(),
              // VistoeraTASK-00217 CASE I — the PRIOR generation's own
              // actual Pitch/Speed (already the backend-normalized value
              // from that earlier call), never the value about to be
              // used for THIS new one.
              pitch: (live.pitch as number | null | undefined) ?? null,
              speed: (live.speed as number | null | undefined) ?? null,
            })
          : priorHistory
      patchFields({
        artifactId: res.data.artifact_id,
        audioAssetId: res.data.audio_asset_id,
        durationMs: res.data.duration_ms,
        status: 'COMPLETED',
        errorMessage: null,
        errorCode: null,
        needsRegeneration: false,
        previousAudioAssetId: null,
        generationHistory: nextHistory,
        // VistoeraTASK-00217 — reflect back the ACTUAL normalized values
        // the Provider ran with (backend may clamp), never the raw
        // pre-clamp Slider value, so the Slider can never silently drift
        // from what really produced this audible result.
        pitch: res.data.pitch ?? null,
        speed: res.data.speed ?? null,
        ...(providerSelectionMode === 'DEFAULT' ? { providerOptionId: null } : {}),
      })
    } catch (err) {
      const apiErr = err as SaasApiError
      const tenantMessage = formatGenerateAudioError(apiErr, t)
      const voiceBindingFailure = isVoiceBindingGenerateAudioError(apiErr)
      setSubmitError({ ...apiErr, message: tenantMessage })
      patchFields({
        status: 'FAILED',
        errorMessage: tenantMessage,
        errorCode: apiErr.code || null,
        ...(voiceBindingFailure ? { selectedVoiceTemplateExecutable: false } : {}),
      })
      if (voiceBindingFailure) {
        setVoiceTemplateExecutable(false)
      }
      message.error(tenantMessage)
    } finally {
      submitLockRef.current = false
    }
  }, [
    canGenerate,
    connectedVoice,
    displayedPitch,
    displayedSpeed,
    effectiveText,
    id,
    patchFields,
    projectId,
    providerSelectionMode,
    selectedSystemVoiceKey,
    selectedVoiceTemplateId,
    t,
    workspaceId,
  ])

  const handleRunClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!canGenerate) return
      if (hasResult) {
        setRegenerateChoiceOpen(true)
        return
      }
      void runGenerate()
    },
    [canGenerate, hasResult, runGenerate],
  )

  const handleDownload = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!previewAudioAssetId) return
      downloadCanvasMedia(canvasAudioPlaybackUrl(workspaceId, previewAudioAssetId))
    },
    [previewAudioAssetId, workspaceId],
  )

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }

  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const handleResultExpandClick = () => {
    nodeData.onExpandFunctionCardDetail?.(id)
  }

  const handleClearText = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (usingConnectedText || isBusy) return
      patchText('')
    },
    [isBusy, patchText, usingConnectedText],
  )

  const handleQuickCreateGenerateVideo = useCallback(() => {
    nodeData.onQuickCreateGenerateVideo?.(id)
  }, [id, nodeData])

  const restoreHistoryEntry = useCallback(
    (entry: AudioGenerationHistoryEntry) => {
      if (!entry.audioAssetId) return
      patchFields({
        audioAssetId: entry.audioAssetId,
        artifactId: entry.artifactId,
        durationMs: entry.durationMs,
        text: entry.textSnapshot ?? localText,
        selectedVoiceTemplateId: entry.voiceTemplateId,
        selectedSystemVoiceKey: entry.systemVoiceKey,
        selectedVoiceLabel: entry.voiceLabel,
        // VistoeraTASK-00217 — restoring a past History entry restores
        // THAT entry's own recorded Pitch/Speed too (falling back to
        // `null` for pre-existing history entries recorded before this
        // task, which then simply resolve via the normal precedence
        // chain instead of fabricating a value that was never recorded).
        pitch: entry.pitch ?? null,
        speed: entry.speed ?? null,
        status: 'COMPLETED',
        needsRegeneration: false,
        errorMessage: null,
        previousAudioAssetId: null,
      })
      setHistoryOpen(false)
    },
    [localText, patchFields],
  )

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [
    cardWidth,
    id,
    showExpanded,
    updateNodeInternals,
    connectedVoice?.ready,
    connectedText?.ready,
    pendingCandidateNodeId,
  ])

  // VistoeraTASK-00222 — expanded body grows with content (no internal
  // scroll). Re-measure section-anchored handles when body height changes.
  useEffect(() => {
    const el = expandedBodyRef.current
    if (!el || !showExpanded) return
    let raf = 0
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => updateNodeInternals(id))
    }
    schedule()
    const ro = new ResizeObserver(schedule)
    ro.observe(el)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [id, showExpanded, updateNodeInternals])

  useEffect(() => {
    if (!connectedVoice?.ready || !connectedVoice.templateId) return
    patchFields({
      connectedVoiceTemplateId: connectedVoice.templateId,
      connectedVoiceLabel: connectedVoice.label,
    })
  }, [connectedVoice, patchFields])

  // VistoeraTASK-00074 P13 — upstream TEXT propagates live via
  // `effectiveText` (recomputed fresh from the live edge graph every
  // render, never cached) for the NEXT generation call already; this
  // additionally marks an already-COMPLETED result stale the moment the
  // connected source's text actually changes, mirroring the EXACT SAME
  // `markStaleIfHasResult` treatment a voice-template swap already gets
  // above — never a silent "still shows the old result as current" gap.
  const prevConnectedTextRef = useRef<string | null>(null)
  const connectedTextMountedRef = useRef(false)
  useEffect(() => {
    const current = connectedText?.ready ? connectedText.text : null
    const prev = prevConnectedTextRef.current
    prevConnectedTextRef.current = current
    if (!connectedTextMountedRef.current) {
      // Skip the very first run (component mount / node load) — this is
      // NOT a live upstream edit, just the initial resolve, so it must
      // never retroactively mark an already-persisted result stale.
      connectedTextMountedRef.current = true
      return
    }
    if (prev === current) return
    const hadResult = status === 'COMPLETED' && !!audioAssetId
    if (hadResult) patchFields(markStaleIfHasResult({}))
  }, [audioAssetId, connectedText, markStaleIfHasResult, patchFields, status])

  const runLabel = isBusy
    ? t('generateAudio.actions.generating')
    : hasResult
      ? t('generateAudio.actions.regenerate')
      : t('generateAudio.actions.generate')

  const resultStatusBadge = useMemo(() => {
    if (isBusy) return t('generateAudio.status.generating')
    if (status === 'FAILED' && !showActivePreview) return t('generateAudio.status.failed')
    if (showActivePreview) return t('generateAudio.result.ready', )
    return t('generateAudio.result.emptyBadge', )
  }, [isBusy, showActivePreview, status, t])

  const collapsedError = !showExpanded && (submitError || status === 'FAILED')
  const durationLabel =
    typeof durationMs === 'number' && durationMs > 0
      ? `${Math.floor(durationMs / 1000 / 60)
          .toString()
          .padStart(2, '0')}:${Math.floor((durationMs / 1000) % 60)
          .toString()
          .padStart(2, '0')}`
      : null

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--audio-generator pcv-node--generate-audio${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)} ${generationSlotIngressShellClass(showExpanded)}${resultZoneActive ? ' is-result-zone-active' : ''}`}
      style={
        {
          width: cardWidth,
          '--pcv-node-preview-height': `${RESULT_AREA_HEIGHT}px`,
        } as CSSProperties
      }
      data-testid="canvas-generate-audio-node"
      data-node-id={id}
      data-expanded={showExpanded ? 'yes' : 'no'}
      data-canonical-order="Header|Result|RelatedAssets|Parameters|InputText|Footer"
      data-voice-template-before-parameters="yes"
      data-parameters-before-input-text="yes"
    >
      <Handle
        type="target"
        position={Position.Left}
        id={GENERIC_INPUT_HANDLE}
        className="pcv-handle pcv-handle--lg"
        style={{ top: handleTop }}
      />
      {/* VistoeraTASK-00074 §18 — the two dedicated section-owned input
       * handles (`AUDIO_VOICE_TEMPLATE_IN_HANDLE`/`AUDIO_TEXT_INPUT_HANDLE`)
       * only ever render their REAL, correctly-positioned `<Handle>`
       * inside the expanded body below (mounted via
       * `VideoInputSectionHandle` next to each section). While collapsed
       * that body is unmounted entirely, so a hidden, non-interactive
       * proxy with the EXACT SAME id is kept mounted here instead —
       * mirrors `ScriptGenerationNode.tsx`'s own
       * `GENERATE_SCRIPT_SOURCE_INPUT_HANDLE` collapsed proxy verbatim
       * (TASK-00058's fix for the "Couldn't create edge for target
       * handle id" warning storm / Canvas-wide crash when a persisted
       * edge's target handle id has no DOM node to resolve a rect
       * from) — never conditionally unmounting BOTH the real and proxy
       * copies of the same id at once. */}
      {!showExpanded ? (
        <>
          <Handle
            type="target"
            position={Position.Left}
            id={AUDIO_VOICE_TEMPLATE_IN_HANDLE}
            className="pcv-handle pcv-handle--aggregate"
            style={{ top: handleTop, visibility: 'hidden', pointerEvents: 'none' }}
            aria-hidden
          />
          <Handle
            type="target"
            position={Position.Left}
            id={AUDIO_TEXT_INPUT_HANDLE}
            className="pcv-handle pcv-handle--aggregate"
            style={{ top: handleTop, visibility: 'hidden', pointerEvents: 'none' }}
            aria-hidden
          />
        </>
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        id="audio-out"
        className="pcv-handle pcv-handle--lg"
        style={{ top: handleTop }}
      />

      <div className="pcv-node-inner">
        {/* ① Header */}
        <CanvasFunctionCardHeader
          className="pcv-gen-audio-header"
          icon={def.icon}
          color={def.color}
          title={displayLabel}
          statusBadge={
            <>
              {needsRegeneration && showActivePreview && !isBusy ? (
                <span className="pcv-brief-header-badge pcv-gen-stale-badge" data-testid="audio-needs-regen-badge">
                  {t('generation.needsRegeneration')}
                </span>
              ) : null}
              {collapsedError ? (
                <span
                  className="pcv-media-error-badge"
                  title={displayErrorMessage || t('generateAudio.status.failed')}
                >
                  {t('generateAudio.status.failedShort')}
                </span>
              ) : null}
            </>
          }
          actions={
            <>
              {showActivePreview ? (
                <Tooltip title={mediaHeader.download} mouseEnterDelay={0.3}>
                  <button
                    type="button"
                    className="pcv-media-hover-btn"
                    aria-label={mediaHeader.download}
                    data-testid="audio-header-download"
                    disabled={!previewAudioAssetId}
                    onClick={handleDownload}
                  >
                    <DownloadOutlined />
                  </button>
                </Tooltip>
              ) : null}
              <Tooltip title={t('generateAudio.history.title', )} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  ref={setHistoryBtnEl}
                  className="pcv-media-hover-btn"
                  data-testid="audio-history-btn"
                  aria-label={t('generateAudio.history.title', )}
                  onClick={(e) => {
                    e.stopPropagation()
                    setHistoryBtnEl(e.currentTarget)
                    setHistoryOpen(true)
                  }}
                >
                  <HistoryOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className="pcv-media-hover-btn"
                  aria-label={mediaHeader.duplicate}
                  onClick={handleCopyClick}
                >
                  <CopyOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className="pcv-media-hover-btn pcv-media-hover-btn--danger"
                  aria-label={mediaHeader.delete}
                  onClick={handleDeleteClick}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </>
          }
        />

        {/* ② Result — always visible (single canonical result carrier, like GenerateImage) */}
        <div
          className={`pcv-gen-result-area pcv-gen-audio-result-area${showActivePreview && playbackUrl ? ' has-active-audio' : ''} ${NODE_EXPAND_TRIGGER_CLASS}`}
          data-testid="audio-result-area"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onClick={handleResultExpandClick}
        >
          <div className="pcv-gen-result-section-label">
            <span>{t('generation.resultSection')}</span>
            <span
              className={`pcv-gen-result-status-badge${showActivePreview && !isBusy ? ' is-ready' : ''}${isBusy ? ' is-busy' : ''}`}
            >
              {resultStatusBadge}
            </span>
            {durationLabel && showActivePreview ? (
              <span className="pcv-gen-audio-result-duration">{durationLabel}</span>
            ) : null}
          </div>

          {showActivePreview && playbackUrl ? (
            <div
              className="pcv-gen-audio-player-wrap nodrag nopan"
              data-testid="audio-current-result-player"
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceAudioPlayer
                playbackUrl={playbackUrl}
                testId="canvas-generate-audio-player"
                lazyLoad
                onPlay={(el) => pauseOtherCanvasAudio(el)}
              />
              {isBusy ? (
                <div className="pcv-gen-audio-result-overlay" data-testid="audio-generating-overlay">
                  <LoadingOutlined spin />
                  <span>{resultStatusBadge}</span>
                </div>
              ) : null}
            </div>
          ) : !showActivePreview && isBusy ? (
            <div className="pcv-gen-result-loading" style={{ minHeight: RESULT_AREA_HEIGHT }} data-testid="audio-generating-empty">
              <LoadingOutlined spin />
              <span>{resultStatusBadge}</span>
            </div>
          ) : !showActivePreview && status === 'FAILED' ? (
            <div className="pcv-gen-result-empty nodrag" style={{ minHeight: RESULT_AREA_HEIGHT }} data-testid="audio-result-error">
              <span>{displayErrorMessage || t('generateAudio.errors.generateFailed')}</span>
              {showRetryAction ? (
                <button type="button" className="pcv-gen-link-btn" onClick={() => void runGenerate()}>
                  {t('generateAudio.actions.retry')}
                </button>
              ) : voiceTemplateUnavailable ? (
                <button
                  type="button"
                  className="pcv-gen-link-btn"
                  data-testid="audio-reselect-voice-template"
                  disabled={isBusy || Boolean(connectedVoice?.ready)}
                  onClick={(e) => {
                    e.stopPropagation()
                    openVoiceTemplateAssetPicker()
                  }}
                >
                  {t('generateAudio.actions.selectVoiceTemplate')}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="pcv-gen-result-empty" style={{ minHeight: RESULT_AREA_HEIGHT }} data-testid="audio-result-empty">
              <span className="pcv-gen-result-empty-icon">
                <SoundOutlined />
              </span>
              <span>{t('generateAudio.preview.empty')}</span>
              <span className="pcv-gen-result-empty-hint">
                {showExpanded
                  ? t('generateAudio.result.emptyHintExpanded')
                  : t('generateAudio.result.emptyHintCollapsed')}
              </span>
            </div>
          )}

          {submitError && status !== 'FAILED' ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
              {formatGenerateAudioError(submitError, t)}
            </div>
          ) : null}
        </div>

        {showVideoQuickAction ? (
          <AudioWorkflowRail
            disabled={audioStaleForVideo}
            disabledReason={audioStaleForVideo ? t('generateAudio.quickAction.regenerateAudioFirst') : undefined}
            onQuickCreateGenerateVideo={handleQuickCreateGenerateVideo}
            onMouseEnter={activateResultZone}
            onMouseLeave={deactivateResultZone}
          />
        ) : null}

        {showExpanded ? (
          <div
            ref={expandedBodyRef}
            className="pcv-brief-body pcv-gen-input-body nodrag"
            onClick={(e) => e.stopPropagation()}
            data-testid="audio-expanded-body"
          >
            {/* ③ Related Assets — Voice Template (ACTIVE_VOICE_TEMPLATE_COUNT=1)
             * VistoeraTASK-00074 §10/§18 — `pcv-video-input-section` gives
             * this section the `position: relative` anchor
             * `VideoInputSectionHandle` needs (reused verbatim from
             * Generate Video's own reference-asset/prompt sections, see
             * `production-canvas.css`), so the handle's `top: 50%` is
             * relative to THIS section's own box, never the whole node. */}
            <div
              className="pcv-brief-section pcv-video-input-section"
              data-testid="audio-related-assets"
              data-video-input-port={AUDIO_VOICE_TEMPLATE_IN_HANDLE}
            >
              <VideoInputSectionHandle
                id={AUDIO_VOICE_TEMPLATE_IN_HANDLE}
                className="pcv-handle pcv-handle--structured-asset"
                testId="audio-voice-template-in-handle"
              />
              <div className="pcv-gen-audio-assets-header">
                <span className="pcv-brief-section-label">
                  {t('generateAudio.assets.label', )}
                  <span className="pcv-gen-audio-assets-sub">
                    {t('generateAudio.assets.voiceTemplate', )}
                  </span>
                </span>
                <button
                  type="button"
                  className="pcv-gen-link-btn"
                  disabled={isBusy || Boolean(connectedVoice?.ready)}
                  data-testid="audio-select-voice-template"
                  onClick={(e) => {
                    e.stopPropagation()
                    openVoiceTemplateAssetPicker()
                  }}
                >
                  {t('generateAudio.assets.selectFromLibrary', )}
                </button>
              </div>

              <div className="pcv-gen-audio-template-row">
                {activeTemplateLabel ? (
                  <div
                    className={`pcv-gen-audio-template-card is-selected${voiceTemplateUnavailable ? ' is-unavailable' : ''}`}
                    data-testid="canvas-generate-audio-connected-voice"
                    data-voice-template-usable={voiceTemplateUnavailable ? 'no' : 'yes'}
                  >
                    {voiceTemplateUnavailable ? (
                      <ExclamationCircleFilled className="pcv-generate-audio-connected-voice-icon is-unavailable" aria-hidden />
                    ) : (
                      <CheckCircleFilled className="pcv-generate-audio-connected-voice-icon" aria-hidden />
                    )}
                    <div className="pcv-gen-audio-template-meta">
                      <span className="pcv-generate-audio-connected-voice-label" data-testid="audio-voice-source-label">
                        {t(voiceSourceLabelKey)}
                      </span>
                      <span className="pcv-generate-audio-connected-voice-name">{activeTemplateLabel}</span>
                      {voiceTemplateUnavailable ? (
                        <span className="pcv-gen-audio-template-unavailable-badge" data-testid="audio-voice-template-unavailable">
                          {t('generateAudio.assets.templateUnavailable')}
                        </span>
                      ) : null}
                    </div>
                    {!connectedVoice?.ready ? (
                      <button
                        type="button"
                        className="pcv-gen-link-btn"
                        disabled={isBusy}
                        data-testid="audio-change-voice-template"
                        onClick={(e) => {
                          e.stopPropagation()
                          openVoiceTemplateAssetPicker()
                        }}
                      >
                        {t('generateAudio.actions.selectVoiceTemplate')}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="pcv-gen-audio-template-card is-empty"
                    data-testid="audio-voice-template-empty"
                    disabled={isBusy || Boolean(connectedVoice?.ready)}
                    onClick={(e) => {
                      e.stopPropagation()
                      openVoiceTemplateAssetPicker()
                    }}
                  >
                    <SoundOutlined />
                    <span>{t('generateAudio.actions.selectVoiceTemplate')}</span>
                  </button>
                )}

                <button
                  type="button"
                  className="pcv-gen-audio-template-card is-add"
                  disabled={isBusy}
                  data-testid="audio-create-voice-template"
                  onClick={(e) => {
                    e.stopPropagation()
                    nodeData.onCreateVoiceTemplateNode?.(id)
                  }}
                >
                  <PlusCircleOutlined />
                  <span>{t('generateAudio.actions.createVoiceTemplate')}</span>
                </button>
              </div>

              {showPendingCandidateHint ? (
                <div className="pcv-generate-audio-candidate-hint">{t('generateAudio.candidate.pending')}</div>
              ) : null}
            </div>

            {/* ④ Parameters — no fake Language/Speed/Format (Canvas API unsupported) */}
            <div className="pcv-gen-params-header" data-testid="audio-params-header">
              <span className="pcv-brief-section-label">
                {t('generateAudio.params.label', )}
              </span>
            </div>
            <div className="pcv-gen-params-primary pcv-gen-audio-params-row" data-testid="audio-params-body">
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">
                  {t('generateAudio.params.voiceIdentity', )}
                </span>
                <span className="pcv-gen-field-chip pcv-gen-audio-identity-chip" data-testid="audio-voice-identity-source">
                  {activeTemplateLabel ||
                    t('generateAudio.params.voiceIdentityUnset', )}
                </span>
              </div>

              {/* VistoeraTASK-00217 — Capability-driven Pitch/Speed.
               * NEVER an `if (provider === 'MINIMAX')` branch: this ONLY
               * ever asks the canonical `parameterCapability.pitch/speed
               * .supported` flag — a Provider that doesn't support one
               * (or hasn't loaded yet) simply never renders that row,
               * never a disabled-but-fabricated slider with made-up
               * bounds. */}
              {pitchCapability?.supported ? (
                <div className="pcv-gen-field-row pcv-gen-audio-param-slider-row" data-testid="audio-param-pitch-row">
                  <span className="pcv-gen-field-label" id={`audio-param-pitch-label-${id}`}>
                    {t('generateAudio.params.pitch.label')}
                  </span>
                  <div className="pcv-gen-audio-slider-wrap">
                    <span className="pcv-gen-audio-slider-scale-label">{t('generateAudio.params.pitch.low')}</span>
                    <input
                      type="range"
                      className="pcv-gen-audio-slider nodrag"
                      aria-labelledby={`audio-param-pitch-label-${id}`}
                      data-testid="audio-param-pitch-slider"
                      min={pitchCapability.min ?? -12}
                      max={pitchCapability.max ?? 12}
                      step={pitchCapability.step ?? 1}
                      value={displayedPitch ?? pitchCapability.default ?? 0}
                      disabled={isBusy}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPitchDraft(Number(e.target.value))}
                      onMouseUp={commitPitch}
                      onTouchEnd={commitPitch}
                      onKeyUp={commitPitch}
                      onBlur={commitPitch}
                    />
                    <span className="pcv-gen-audio-slider-scale-label">{t('generateAudio.params.pitch.high')}</span>
                  </div>
                  <span className="pcv-gen-field-chip pcv-gen-audio-param-value" data-testid="audio-param-pitch-value">
                    {(() => {
                      const v = displayedPitch ?? pitchCapability.default ?? 0
                      return v > 0 ? `+${v}` : `${v}`
                    })()}
                  </span>
                </div>
              ) : null}

              {speedCapability?.supported ? (
                <div className="pcv-gen-field-row pcv-gen-audio-param-slider-row" data-testid="audio-param-speed-row">
                  <span className="pcv-gen-field-label" id={`audio-param-speed-label-${id}`}>
                    {t('generateAudio.params.speed.label')}
                  </span>
                  <div className="pcv-gen-audio-slider-wrap">
                    <span className="pcv-gen-audio-slider-scale-label">{t('generateAudio.params.speed.slow')}</span>
                    <input
                      type="range"
                      className="pcv-gen-audio-slider nodrag"
                      aria-labelledby={`audio-param-speed-label-${id}`}
                      data-testid="audio-param-speed-slider"
                      min={speedCapability.min ?? 0.5}
                      max={speedCapability.max ?? 2}
                      step={speedCapability.step ?? 0.01}
                      value={displayedSpeed ?? speedCapability.default ?? 1}
                      disabled={isBusy}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setSpeedDraft(Number(e.target.value))}
                      onMouseUp={commitSpeed}
                      onTouchEnd={commitSpeed}
                      onKeyUp={commitSpeed}
                      onBlur={commitSpeed}
                    />
                    <span className="pcv-gen-audio-slider-scale-label">{t('generateAudio.params.speed.fast')}</span>
                  </div>
                  <span className="pcv-gen-field-chip pcv-gen-audio-param-value" data-testid="audio-param-speed-value">
                    {(displayedSpeed ?? speedCapability.default ?? 1).toFixed(2)}×
                  </span>
                </div>
              ) : null}

              <p className="pcv-gen-audio-params-hint">
                {t('generateAudio.params.hint')}
              </p>
            </div>

            {/* ⑤ Input Text — MUST be after Parameters
             * VistoeraTASK-00074 §10/§18 — same section-anchored handle
             * pattern as ③ above, targeting `AUDIO_TEXT_INPUT_HANDLE` so a
             * dropped edge lands precisely on THIS textarea's section,
             * never the node's geometric center/Parameters/Footer. */}
            <div
              className="pcv-brief-section pcv-video-input-section"
              data-testid="audio-input-text-section"
              data-video-input-port={AUDIO_TEXT_INPUT_HANDLE}
            >
              <VideoInputSectionHandle
                id={AUDIO_TEXT_INPUT_HANDLE}
                className="pcv-handle pcv-handle--prompt"
                testId="audio-text-in-handle"
              />
              <span className="pcv-brief-section-label">{t('generateAudio.sections.textInput')}</span>
              {usingConnectedText ? (
                <div className="pcv-generate-audio-connected-text">
                  {t('generateAudio.sections.fromConnection')}: {connectedText?.sourceLabel}
                </div>
              ) : null}
              <textarea
                className="pcv-brief-textarea nodrag pcv-canvas-scrollable"
                data-testid="audio-input-text"
                value={usingConnectedText ? connectedText?.text || '' : localText}
                onChange={(e) => patchText(e.target.value)}
                placeholder={t('generateAudio.placeholders.text')}
                disabled={isBusy || usingConnectedText}
                maxLength={CANVAS_AUDIO_TEXT_MAX_LENGTH}
                rows={5}
              />
              <div className="pcv-gen-audio-text-tools">
                <span className="pcv-brief-textarea-counter">
                  {(usingConnectedText ? connectedText?.text.length || 0 : localText.length)}/{CANVAS_AUDIO_TEXT_MAX_LENGTH}
                </span>
                <button
                  type="button"
                  className="pcv-gen-link-btn"
                  disabled={isBusy || usingConnectedText || !localText}
                  data-testid="audio-clear-text"
                  onClick={handleClearText}
                >
                  {t('generateAudio.actions.clearText', )}
                </button>
              </div>
            </div>

            {/* ⑥ Footer — Provider | Skill | Generate (no Diverge) */}
            <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()} data-testid="audio-footer">
              <Tooltip
                title={
                  providerSelectOptions.find((o) => o.value === resolvedProviderOptionId)?.title ||
                  t('generateAudio.provider.label', )
                }
                mouseEnterDelay={0.4}
              >
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                  popupClassName="pcv-gen-field-select-popup"
                  data-testid="audio-provider-select"
                  aria-label={t('generateAudio.provider.label', )}
                  value={resolvedProviderOptionId ?? undefined}
                  disabled={isBusy || providerOptionsLoading || (providerOptionsError && providerSelectOptions.length === 0) || providerSelectOptions.length === 0}
                  placeholder={
                    providerOptionsLoading
                      ? t('generateAudio.provider.loading', )
                      : providerOptionsError && providerSelectOptions.length === 0
                        ? t('generateAudio.provider.loadFailed', )
                        : t('generateAudio.provider.placeholder', )
                  }
                  options={providerSelectOptions}
                  onChange={(v) => {
                    patchFields(markStaleIfHasResult(buildExplicitProviderSelectionPatch(v)))
                  }}
                  popupMatchSelectWidth={false}
                  placement="bottomLeft"
                  getPopupContainer={() => document.body}
                />
              </Tooltip>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                data-testid="audio-skill-select"
                value={skillSelectionSource === 'EXPLICIT' ? AUDIO_SKILL_KEY : 'AUTO'}
                options={[
                  { value: 'AUTO', label: t('generateAudio.skill.auto', ) },
                  {
                    value: AUDIO_SKILL_KEY,
                    label: t('generateAudio.skill.v1', ),
                  },
                ]}
                onChange={(v) =>
                  patchFields(
                    markStaleIfHasResult(
                      v === 'AUTO'
                        ? { skillSelectionSource: 'AUTO', skillKey: null, skillVersion: null }
                        : {
                            skillSelectionSource: 'EXPLICIT',
                            skillKey: AUDIO_SKILL_KEY,
                            skillVersion: AUDIO_SKILL_VERSION,
                          },
                    ),
                  )
                }
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
              <Tooltip
                title={
                  !canGenerate && blockingHint
                    ? blockingHint
                    : !voiceValid
                      ? t('generateAudio.errors.selectVoiceTemplate')
                      : undefined
                }
              >
                <button
                  ref={runBtnRef}
                  type="button"
                  className="pcv-gen-footer-generate-btn"
                  data-testid="audio-generate-btn"
                  data-disabled-reason={disabledReasonCode || undefined}
                  disabled={!canGenerate}
                  aria-disabled={!canGenerate}
                  onClick={handleRunClick}
                >
                  {isBusy ? <LoadingOutlined spin /> : <ThunderboltOutlined />} {runLabel}
                </button>
              </Tooltip>
            </div>
            {!canGenerate && blockingHint && !isBusy ? (
              <div className="pcv-gen-footer-hint" data-testid="audio-readiness-hint">
                {blockingHint}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <CanvasActionPopover
        anchorEl={historyBtnEl}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('generateAudio.history.title', )}
        width={300}
      >
        {generationHistory.length === 0 ? (
          <div className="pcv-gen-history-empty">
            {t('generateAudio.history.empty', )}
          </div>
        ) : (
          <div className="pcv-gen-history-list" data-testid="audio-history-list">
            {[...generationHistory].reverse().map((h) => (
              <button
                key={`${h.version}-${h.audioAssetId || h.artifactId}`}
                type="button"
                className="pcv-gen-history-item nodrag"
                onClick={() => restoreHistoryEntry(h)}
              >
                <span className="pcv-gen-history-version">V{h.version}</span>
                <span className="pcv-gen-history-meta">
                  {h.voiceLabel || h.voiceTemplateId || h.systemVoiceKey || ''}
                  {h.completedAt ? ` · ${new Date(h.completedAt).toLocaleString()}` : ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </CanvasActionPopover>

      <CanvasActionPopover
        anchorEl={runBtnRef.current}
        open={regenerateChoiceOpen}
        onClose={() => setRegenerateChoiceOpen(false)}
        title={t('generateAudio.regenerate.title')}
      >
        <p className="pcv-action-popover-desc">{t('generateAudio.regenerate.description')}</p>
        <div className="pcv-action-popover-actions">
          <button
            type="button"
            className="pcv-action-popover-btn"
            onClick={() => {
              setRegenerateChoiceOpen(false)
              nodeData.onDuplicateForRegenerate?.(id)
            }}
          >
            {t('generateAudio.regenerate.separate')}
          </button>
          <button
            type="button"
            className="pcv-action-popover-btn pcv-action-popover-btn--primary"
            onClick={() => {
              setRegenerateChoiceOpen(false)
              void runGenerate()
            }}
          >
            {t('generateAudio.regenerate.overwrite')}
          </button>
        </div>
      </CanvasActionPopover>
    </div>
  )
}

const GenerateAudioNode = memo(GenerateAudioNodeImpl)
export default GenerateAudioNode
